import { FC, useCallback, useMemo } from 'react';

import { IModifierTypeUIProps } from 'features/menu/components/product-detail/modifier/types';
import {
  getMultiplierLabel,
  getSelectedMultiplier,
  isMultiplierVisuallySelected,
  logProductModifierCustomization,
} from 'features/menu/components/product-detail/modifier/utils';

import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';

import { ModifierListItem } from './modifier-list-item';

export const ModifierNonNumeric: FC<IModifierTypeUIProps> = ({
  modifier,
  selections,
  onSelectionsChange,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const selectedMultiplier = getSelectedMultiplier(modifier, selections);
  const selectedMultiplierIsZeroMultiplier = selectedMultiplier.multiplier === 0;
  const isSelected = isMultiplierVisuallySelected(selectedMultiplier);

  const allOptionsHavePrefix = modifier.multipliers.every(multiplier => !!multiplier.prefix);

  const badgeLabel = getMultiplierLabel(allOptionsHavePrefix, selectedMultiplier);

  const radioListOptions = useMemo(() => {
    return modifier.multipliers.map(multiplier => {
      const label = getMultiplierLabel(allOptionsHavePrefix, multiplier);
      const price = multiplier.price ?? 0;
      return {
        label:
          multiplier.multiplier === 1
            ? t('regMultiplierName', { defaultValue: 'Reg.' })
            : label || '',
        labelSuffix: price > 0 ? `+${formatCurrencyForLocale(price)}` : '',
        value: multiplier.id,
        multiplier: multiplier.multiplier,
      };
    });
  }, [allOptionsHavePrefix, formatCurrencyForLocale, modifier]);

  const onOptionPress = useCallback(
    (_: unknown, optionValue: string) => {
      onSelectionsChange({ [optionValue]: 1 });

      const selectedMultiplier = modifier.multipliers.find(m => m.id === optionValue);
      logProductModifierCustomization(selectedMultiplier?.name ?? '', modifier.name, optionValue);
    },
    [onSelectionsChange, modifier]
  );

  const onCardPress = useCallback(() => {
    const multiplierForSelection = modifier.multipliers.find(
      m => m.multiplier === (isSelected ? 0 : 1)
    );

    if (!multiplierForSelection) {
      return;
    }

    onSelectionsChange({ [multiplierForSelection.id]: 1 });

    logProductModifierCustomization(
      multiplierForSelection.name ?? '',
      modifier.name,
      multiplierForSelection.id
    );
  }, [isSelected, modifier, onSelectionsChange]);

  return (
    <ModifierListItem
      isSelected={isSelected}
      // @ts-ignore
      badgeContent={!selectedMultiplierIsZeroMultiplier ? badgeLabel : ''}
      content={modifier}
      selectedOption={selectedMultiplier.id}
      itemValue={selectedMultiplier}
      options={radioListOptions}
      onOptionPress={onOptionPress}
      onCardPress={onCardPress}
    />
  );
};
